import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink } from "react-router-dom";
import { api } from "../lib/api";
import { useEffectiveUser } from "../lib/auth";
import { canSeeBeta } from "../lib/permissions";

// Top-of-page banner shown when a shift-bid sheet is currently
// "live" (opened_at set, not locked) for the signed-in officer's
// department + home shift AND they haven't yet claimed a row on
// that sheet. Disappears the moment they claim; reappears if a
// more-senior officer bumps them (their claim goes back to null,
// the sheet still counts as un-claimed on the officer's side).
//
// Beta-gated the same way the Shift Bid page is — non-beta users
// never see either surface.
export function ShiftBidLiveBanner() {
  const me = useEffectiveUser();

  // Banner is gated to beta testers, mirroring the Shift Bid page
  // + route. Non-beta officers in an agency where a beta L2 opened
  // a sheet would otherwise see the nudge but hit the route guard
  // and bounce — noise for no purpose.
  const seeBeta = canSeeBeta(me);
  const openSheets = useQuery({
    queryKey: ["openShiftBidSheets"],
    queryFn: () => api.listOpenShiftBidSheets(),
    enabled: !!me && seeBeta,
    // Auto-refetch on window focus so a bumped officer sees the
    // banner reappear when they return to the tab / app.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const employees = useQuery({
    queryKey: ["employees"],
    queryFn: () => api.listEmployees(),
    enabled: !!me?.employeeId,
    staleTime: 60_000,
  });

  const myEmp = useMemo(
    () => (employees.data ?? []).find((e) => e.id === me?.employeeId) ?? null,
    [employees.data, me?.employeeId],
  );

  // Filter to sheets in my department. If my dept doesn't have
  // shift subdivision (row.shift_id null on the sheet), any row
  // is eligible; otherwise we still show the banner (per-row
  // eligibility gets checked on the page itself).
  const relevantSheets = useMemo(
    () => (openSheets.data ?? []).filter(
      (s) => myEmp && s.departmentId === myEmp.departmentId,
    ),
    [openSheets.data, myEmp],
  );

  const myClaims = useQuery({
    queryKey: ["myShiftBidClaims", relevantSheets.map((s) => s.id).join(",")],
    queryFn: () => api.listMyShiftBidClaims(relevantSheets.map((s) => s.id)),
    enabled: relevantSheets.length > 0,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  // Unclaimed sheets = open sheets in my dept where I don't yet
  // hold a row. The banner shows the FIRST one; if the officer
  // has multiple open bids across their sheets (rare) they'll see
  // one at a time.
  const unclaimed = useMemo(() => {
    const held = new Set((myClaims.data ?? []).map((r) => r.sheetId));
    return relevantSheets.filter((s) => !held.has(s.id));
  }, [relevantSheets, myClaims.data]);

  if (!seeBeta) return null;
  if (unclaimed.length === 0) return null;
  return (
    <div className="shrink-0 border-b border-sky-300 bg-sky-50 px-3 py-1.5 text-sm font-medium text-sky-900 text-center print:hidden">
      <span className="mr-2 inline-block px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-sky-200 text-sky-900 align-middle">
        Live
      </span>
      Shift Bid is open for your shift —{" "}
      <NavLink to="/shift-bid" className="underline font-semibold hover:text-sky-700">
        pick your row →
      </NavLink>
    </div>
  );
}
